import { Component, HostListener, signal, computed, inject, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink, Router } from '@angular/router';
import { CartService } from '../../services/cart.service';
import { WishlistService } from '../../services/wishlist.service';
import { StoreDataService } from '../../services/store-data';
import { CustomerAuthService } from '../../services/auth.service';
import { toSlug } from '../../utils/slug';

interface DrawerTile   { label: string; img: string; path: string; }
interface DrawerSection { name: string; tiles: DrawerTile[]; }

interface DrawerContent {
  featured:    DrawerSection[];
  categories:  DrawerTile[];
  accessories: DrawerTile[];
}

const SM = (file: string) =>
  `https://sava-s3.in-maa-1.linodeobjects.com/mmtees-dev/marketing/side_menu/${file}`;

@Component({
  selector: 'app-navbar',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class Navbar {
  private cartService = inject(CartService);
  private wishlistService = inject(WishlistService);
  get wishlistCount() { return this.wishlistService.count(); }
  private store = inject(StoreDataService);
  private router = inject(Router);
  readonly auth = inject(CustomerAuthService);
  logout() { this.auth.logout(); this.closeAccountDropdown(); this.router.navigate(['/']); }
  readonly toSlug = toSlug;
  get popularProducts() { return this.store.getNewArrivals('men').slice(0, 4); }
  isScrolled   = signal(false);
  get cartCount() { return this.cartService.count(); }
  drawerOpen   = signal(false);
  activeDrawerTab   = signal('MEN');
  expandedAccordions = signal<Set<string>>(new Set(['categories']));

  // ── Search overlay ──────────────────────────────────────────────────────
  searchOpen  = signal(false);
  searchQuery = signal('');

  readonly searchSuggestions: { label: string; route: string }[] = [
    { label: 'Batman T-Shirts',    route: '/men-tshirts'          },
    { label: 'Oversized T-Shirts', route: '/men-tshirts'          },
    { label: 'Harry Potter',       route: '/trending-fandoms'     },
    { label: 'Marvel',             route: '/trending-fandoms'     },
    { label: 'Attack on Titan',    route: '/trending-fandoms'     },
    { label: 'Joggers',            route: '/men/joggers'          },
    { label: 'Polos',              route: '/men/polo-tshirts'     },
    { label: 'Cotton Linen Shirts',route: '/men/shirts'           },
    { label: 'Jeans',              route: '/men/men-jeans'        },
    { label: 'Dresses',            route: '/women/dresses'        },
  ];

  // On focus (empty query) show the full recommendations; once typing, narrow them down.
  get filteredSuggestions() {
    const q = this.searchQuery().toLowerCase().trim();
    if (!q) return this.searchSuggestions;
    return this.searchSuggestions.filter(s => s.label.toLowerCase().includes(q));
  }

  // Token + normalized match so "spiderman" finds "Spider-Man", multi-word queries AND together.
  private matchProduct(name: string, category: string, q: string): boolean {
    const hay = (name + ' ' + category).toLowerCase();
    const hayNorm = hay.replace(/[^a-z0-9]/g, '');
    return q.split(/\s+/).filter(Boolean).every((tok) => {
      const t = tok.replace(/[^a-z0-9]/g, '');
      return hay.includes(tok) || (!!t && hayNorm.includes(t));
    });
  }

  // Live results pulled from the real catalog (all genders) — empty until the user types.
  readonly searchResults = computed(() => {
    const q = this.searchQuery().toLowerCase().trim();
    if (!q) return [];
    const all = this.store.allProducts();
    const seen = new Set<string>();
    const out: { name: string; category: string; img: string; slug: string }[] = [];
    for (const p of all) {
      if (out.length >= 6) break;
      const slug = p.slug || toSlug(p.name);
      if (seen.has(slug)) continue;
      if (this.matchProduct(p.name, p.category, q)) {
        seen.add(slug);
        out.push({ name: p.name, category: p.category, img: p.img, slug });
      }
    }
    return out;
  });

  // True only while the user has typed something — drives whether the dropdown shows at all.
  readonly hasQuery = computed(() => this.searchQuery().trim().length > 0);

  openSearch()  { this.searchOpen.set(true); this.drawerOpen.set(false); }
  closeSearch() { this.searchOpen.set(false); this.searchQuery.set(''); }
  // Close the dropdown when focus leaves the bar — deferred so a click on a suggestion/result
  // (which blurs the input first) still registers before the panel is removed.
  onSearchBlur() { setTimeout(() => this.searchOpen.set(false), 150); }

  selectSuggestion(route: string) {
    this.closeSearch();
    this.router.navigate([route]);
  }

  submitSearch() {
    const q = this.searchQuery().trim();
    if (!q) return;
    this.closeSearch();
    this.router.navigate(['/search'], { queryParams: { q } });
  }

  readonly navLinks = [
    { label: 'MEN',   path: '/men'   },
    { label: 'WOMEN', path: '/women' },
    { label: 'ACCESSORIES',  path: '/accessories'  },
  ];

  isNavActive(path: string): boolean {
    const url = this.router.url.split('?')[0];
    if (path === '/men')   return url === '/men'   || url.startsWith('/men/')   || url.startsWith('/men-');
    if (path === '/women') return url === '/women' || url.startsWith('/women/') || url.startsWith('/women-');
    if (path === '/accessories')  return url === '/accessories'  || url.startsWith('/accessories/')  || url.startsWith('/accessories-');
    return url === path;
  }

  readonly drawerTabs = ['MEN', 'WOMEN', 'ACCESSORIES'];

  readonly drawerData: Record<string, DrawerContent> = {

    // ── MEN ─────────────────────────────────────────────────────────────────
    MEN: {
      featured: [
        { name: '', tiles: [
          { label: 'Hot Merch',        img: SM('Untitled_design_5_5ia81yw.png'), path: '/trending-fandoms'        },
          { label: 'Collectibles',     img: SM('Untitled_design_6_16WQGsn.png'), path: '/explore/collectibles'    },
          { label: 'Culture Code',     img: SM('Untitled_design_7_NBQbKS7.png'), path: '/culture-code-collection' },
          { label: 'Marvel',           img: SM('Carousel_kdxABMS.png'),           path: '/trending-fandoms'        },
          { label: 'Harry Potter',     img: SM('Carousel_1.jpg'),                 path: '/trending-fandoms'        },
          { label: 'New Arrivals',     img: SM('Untitled_design_10.png'),          path: '/men'                    },
          { label: 'Anime Collection', img: SM('Untitled_design_8_YxkHvPh.png'), path: '/trending-fandoms'        },
          { label: 'Cult Classics',    img: SM('Untitled_design_3_c0XBebi.jpg'),  path: '/souled-cult'             },
        ]},
        { name: "Summer '26", tiles: [
          { label: 'Summer Shirts', img: SM('1_jrM7QjC.png'),  path: '/men/shirts'       },
          { label: 'T-shirts',      img: SM('2_B342pD3.png'),  path: '/men-tshirts'      },
          { label: 'Cotton Linen',  img: SM('1_jrM7QjC.png'),  path: '/men/shirts'       },
          { label: 'Polos',         img: SM('2_B342pD3.png'),  path: '/men/polo-tshirts' },
          { label: 'Easy Pants',    img: SM('1_jrM7QjC.png'),  path: '/men/joggers'      },
        ]},
      ],
      categories: [
        { label: 'Shirts',       img: SM('1_jrM7QjC.png'),                path: '/men/shirts'       },
        { label: 'T-Shirts',     img: SM('2_B342pD3.png'),                path: '/men-tshirts'      },
        { label: 'Cotton Linen', img: SM('1_jrM7QjC.png'),                path: '/men/shirts'       },
        { label: 'Polos',        img: SM('2_B342pD3.png'),                path: '/men/polo-tshirts' },
        { label: 'Jeans',        img: SM('Untitled_design_6_16WQGsn.png'),path: '/men/men-jeans'    },
        { label: 'Joggers',      img: SM('Untitled_design_5_5ia81yw.png'),path: '/men/joggers'      },
        { label: 'Footwear',     img: SM('Raptors-V2-Canyon-Dust_UZSNY6U.jpg'), path: '/men/footwear' },
        { label: 'Accessories',  img: SM('Untitled_design_7_NBQbKS7.png'),path: '/all-accessories'  },
        { label: 'Collectibles', img: SM('Untitled_design_3_c0XBebi.jpg'),path: '/explore/collectibles' },
      ],
      accessories: [
        { label: 'Collectibles', img: SM('Untitled_design_3_c0XBebi.jpg'), path: '/explore/collectibles' },
        { label: 'Caps',         img: SM('Untitled_design_7_NBQbKS7.png'),  path: '/all-accessories'      },
        { label: 'Socks',        img: SM('Untitled_design_5_5ia81yw.png'),  path: '/all-accessories'      },
        { label: 'Perfumes',     img: SM('Untitled_design_10.png'),          path: '/all-accessories'      },
        { label: 'Backpacks',    img: SM('Untitled_design_8_YxkHvPh.png'),  path: '/all-accessories'      },
      ],
    },

    // ── WOMEN ────────────────────────────────────────────────────────────────
    WOMEN: {
      featured: [
        { name: '', tiles: [
          { label: 'Trending',     img: SM('Untitled_design_I7ncEQz.jpg'), path: '/women'               },
          { label: 'New Arrivals', img: SM('BOTTOMWEAR_1.jpg'),             path: '/women'               },
          { label: 'Kawaii',       img: SM('Untitled_design_3_hSZGN6I.jpg'),path: '/women/dresses'       },
          { label: 'Office Edit',  img: SM('BOTTOMWEAR_3.jpg'),             path: '/women/dresses'       },
          { label: 'Witty Prints', img: SM('Tile_200.jpg'),                 path: '/trending-fandoms'    },
        ]},
        { name: "Summer Picks", tiles: [
          { label: 'Summer Picks',  img: SM('BOTTOMWEAR.jpg'),       path: '/women/dresses'        },
          { label: 'Dresses',       img: SM('DRESSES.jpg'),           path: '/women/dresses'        },
          { label: 'Cotton Linen',  img: SM('SHIRTS_sDsH1B2.jpg'),   path: '/women/dresses'        },
          { label: 'Tops',          img: SM('TOPS.jpg'),              path: '/women/tshirts'        },
          { label: 'Shorts',        img: SM('BOTTOMWEAR_XNd6dZk.jpg'),path: '/women/jeans'         },
          { label: 'Shirts',        img: SM('BOTTOMWEAR_omiTxFJ.jpg'),path: '/women/jeans'         },
        ]},
      ],
      categories: [
        { label: 'Dresses',     img: SM('DRESSES.jpg'),            path: '/women/dresses'        },
        { label: 'Tops',        img: SM('TOPS.jpg'),               path: '/women/tshirts'        },
        { label: 'Shirts',      img: SM('SHIRTS_sDsH1B2.jpg'),    path: '/women/jeans'          },
        { label: 'Jeans',       img: SM('BOTTOMWEAR.jpg'),         path: '/women/jeans'          },
        { label: 'Shorts',      img: SM('BOTTOMWEAR_XNd6dZk.jpg'),path: '/women/jeans'          },
        { label: 'Joggers',     img: SM('BOTTOMWEAR_omiTxFJ.jpg'),path: '/women/jeans'          },
        { label: 'T-Shirts',    img: SM('BOTTOMWEAR_1.jpg'),       path: '/women/tshirts'        },
        { label: 'Accessories', img: SM('BOTTOMWEAR_3.jpg'),       path: '/all-accessories'      },
      ],
      accessories: [
        { label: 'Bags',         img: SM('BOTTOMWEAR_3.jpg'),       path: '/all-accessories'      },
        { label: 'Perfumes',     img: SM('BOTTOMWEAR_1.jpg'),       path: '/all-accessories'      },
        { label: 'Collectibles', img: SM('Tile_200.jpg'),            path: '/explore/collectibles' },
        { label: 'Caps',         img: SM('DRESSES.jpg'),             path: '/all-accessories'      },
        { label: 'Socks',        img: SM('TOPS.jpg'),                path: '/all-accessories'      },
      ],
    },

    // ── ACCESSORIES ─────────────────────────────────────────────────────────────────
    ACCESSORIES: {
      featured: [
        { name: '', tiles: [
          { label: 'T-Shirts',     img: SM('Untitled_design_9.png'),           path: '/accessories' },
          { label: 'Dresses',      img: SM('Untitled_design_3_hSZGN6I.jpg'),   path: '/accessories' },
          { label: 'Sets',         img: SM('Untitled_design_10.png'),           path: '/accessories' },
          { label: 'Marvel',       img: SM('Carousel_kdxABMS.png'),             path: '/accessories' },
          { label: 'Harry Potter', img: SM('Carousel_1.jpg'),                   path: '/accessories' },
          { label: 'Collectibles', img: SM('Untitled_design_3_c0XBebi.jpg'),    path: '/explore/collectibles' },
        ]},
        { name: 'New Arrivals', tiles: [
          { label: 'Shorts',       img: SM('BOTTOMWEAR_XNd6dZk.jpg'),  path: '/accessories' },
          { label: 'Joggers',      img: SM('BOTTOMWEAR_omiTxFJ.jpg'),  path: '/accessories' },
          { label: 'Tops',         img: SM('TOPS.jpg'),                path: '/accessories' },
          { label: 'Tees',         img: SM('2_B342pD3.png'),           path: '/accessories' },
          { label: 'Accessories',  img: SM('Untitled_design_7_NBQbKS7.png'), path: '/all-accessories' },
        ]},
      ],
      categories: [
        { label: 'T-Shirts',    img: SM('Untitled_design_9.png'),         path: '/accessories'                 },
        { label: 'Dresses',     img: SM('Untitled_design_3_hSZGN6I.jpg'), path: '/accessories'                 },
        { label: 'Sets',        img: SM('Untitled_design_10.png'),         path: '/accessories'                 },
        { label: 'Shorts',      img: SM('BOTTOMWEAR_XNd6dZk.jpg'),        path: '/accessories'                 },
        { label: 'Joggers',     img: SM('BOTTOMWEAR_omiTxFJ.jpg'),        path: '/accessories'                 },
        { label: 'Accessories', img: SM('Untitled_design_7_NBQbKS7.png'), path: '/all-accessories'      },
        { label: 'Collectibles',img: SM('Untitled_design_3_c0XBebi.jpg'), path: '/explore/collectibles' },
      ],
      accessories: [
        { label: 'Backpacks',    img: SM('Untitled_design_8_YxkHvPh.png'), path: '/all-accessories'      },
        { label: 'Caps',         img: SM('Untitled_design_7_NBQbKS7.png'), path: '/all-accessories'      },
        { label: 'Socks',        img: SM('Untitled_design_5_5ia81yw.png'), path: '/all-accessories'      },
        { label: 'Collectibles', img: SM('Untitled_design_3_c0XBebi.jpg'), path: '/explore/collectibles' },
      ],
    },
  };

  get currentContent(): DrawerContent {
    return this.drawerData[this.activeDrawerTab()];
  }

  /** Maps the active drawer tab to a storefront gender slug. */
  private tabGender(): string {
    return this.activeDrawerTab() === 'WOMEN' ? 'women'
      : this.activeDrawerTab() === 'ACCESSORIES' ? 'accessories' : 'men';
  }

  /** "Shop by Category" tiles, derived from the LIVE categories collection (admin-managed) so new
      categories appear in the drawer automatically. Falls back to the curated tile image by label
      (then a default) when a live category has no image. The curated featured/accessories sections
      above/below stay hand-built. */
  get drawerCategories(): DrawerTile[] {
    const gender = this.tabGender();
    const curated = this.currentContent.categories;
    const fallbackImg = (label: string) =>
      curated.find(c => c.label.toLowerCase() === label.toLowerCase())?.img
      ?? curated[0]?.img
      ?? SM('Untitled_design_10.png');
    return this.store.getCategories(gender).map(c => ({
      label: c.name,
      img: c.img || fallbackImg(c.name),
      path: c.link,
    }));
  }

  @HostListener('window:scroll')
  onScroll() { this.isScrolled.set(window.scrollY > 4); }

  // ── Account dropdown ───────────────────────────────────────────────────────
  accountDropdownOpen = signal(false);
  openAccountDropdown()  { this.accountDropdownOpen.set(true); }
  closeAccountDropdown() { this.accountDropdownOpen.set(false); }
  /** Clicking the account icon goes to the account page (or login when signed out). */
  goToAccount() {
    this.closeAccountDropdown();
    this.router.navigate([this.auth.isAuthenticated() ? '/my-account' : '/login']);
  }

  // Official Merch drawer accordion — links to the live fandom/collectible collection pages.
  readonly officialMerchLinks = [
    { label: 'Trending Fandoms', path: '/trending-fandoms'        },
    { label: 'Collectibles',     path: '/explore/collectibles'    },
    { label: 'Culture Code',     path: '/culture-code-collection' },
    { label: 'Cult Classics',    path: '/souled-cult'             },
  ];

  readonly accountMenuItems = [
    { label: 'Orders',        path: '/my-account/orders'       },
    { label: 'Gift Vouchers', path: '/my-account/gift-vouchers' },
    { label: 'Wallet',        path: '/my-account/wallet'        },
    { label: 'Reward Points', path: '/my-account/reward-points' },
    { label: 'Profile',       path: '/my-account'              },
    { label: 'FAQs',          path: '/faqs'                    },
    { label: 'Contact Us',    path: '/contact-us'              },
  ];

  openDrawer()  { this.drawerOpen.set(true); this.expandedAccordions.set(new Set(['categories'])); }
  closeDrawer() { this.drawerOpen.set(false); this.expandedAccordions.set(new Set()); }

  setDrawerTab(tab: string) {
    this.activeDrawerTab.set(tab);
    this.expandedAccordions.set(new Set());
  }

  toggleAccordion(name: string) {
    const s = new Set(this.expandedAccordions());
    s.has(name) ? s.delete(name) : s.add(name);
    this.expandedAccordions.set(s);
  }
  isExpanded(name: string) { return this.expandedAccordions().has(name); }
}
